import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const ROLE_LABELS = { owner: "Propriétaire", admin: "Administrateur", accountant: "Comptable", sales: "Commercial", viewer: "Lecture seule" };
const ROLE_OPTIONS = [
  { value: "admin", label: "Administrateur", desc: "Tout sauf facturation et suppression de l'entreprise. Peut gérer l'équipe.", short: "Tout sauf facturation" },
  { value: "accountant", label: "Comptable", desc: "Lecture de toutes les données + exports comptables (FEC, Excel). Idéal pour votre expert-comptable.", short: "Lecture + exports comptables" },
  { value: "sales", label: "Commercial", desc: "Crée et modifie clients, prospects, devis. Ne peut pas créer de factures ni voir la facturation.", short: "Devis, clients, prospects" },
  { value: "viewer", label: "Lecture seule", desc: "Consulte tout en lecture seule. Aucune modification possible.", short: "Consultation uniquement" },
];

function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0,2).map(x=>x[0]).join("").toUpperCase();
}

export default function Team() {
  const { user, org } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("sales");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api("/org/team");
      setMembers(r.members);
      setInvites(r.invites);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const canManage = user?.role === "owner" || user?.role === "admin";

  const invite = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      await api("/org/invite", { method: "POST", body: { email: inviteEmail.trim().toLowerCase(), role: inviteRole } });
      setMsg(`Invitation envoyée à ${inviteEmail}`);
      setInviteEmail(""); setInviteOpen(false);
      await load();
    } catch (e) { setErr(e.message || "Erreur"); }
    setLoading(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Retirer ce membre de l'équipe ?")) return;
    try {
      await api(`/org/team/${id}`, { method: "DELETE" });
      await load();
    } catch (e) { setErr(e.message || "Erreur"); }
  };

  const cancelInvite = async (id) => {
    if (!window.confirm("Annuler cette invitation ? Le lien envoyé ne fonctionnera plus.")) return;
    try {
      await api(`/org/invites/${id}`, { method: "DELETE" });
      setMsg("Invitation annulée.");
      await load();
    } catch (e) { setErr(e.message || "Erreur"); }
  };

  const resendInvite = async (id, email) => {
    try {
      await api(`/org/invites/${id}/resend`, { method: "POST" });
      setMsg(`Invitation renvoyée à ${email}.`);
      await load();
    } catch (e) { setErr(e.message || "Erreur"); }
  };

  const changeRole = async (id, newRole) => {
    try {
      await api(`/org/team/${id}/role`, { method: "PATCH", body: { role: newRole } });
      await load();
    } catch (e) { setErr(e.message || "Erreur"); }
  };

  return (
    <div className="content">
      <div style={{display:'flex', alignItems:'flex-end', marginBottom:30, gap:20, flexWrap:'wrap'}}>
        <div>
          <h1 style={{fontSize:30, marginBottom:6}}>Équipe</h1>
          <p style={{color:'var(--ink-muted)'}}>Membres de <strong>{org?.name}</strong> ({members.length} · {invites.length} invitation{invites.length>1?"s":""} en attente)</p>
        </div>
        <div style={{marginLeft:'auto', display:'flex', gap:8}}>
          {canManage && (
            <button className="btn btn-primary" onClick={()=>setInviteOpen(!inviteOpen)} data-testid="invite-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Inviter un membre
            </button>
          )}
        </div>
      </div>

      {msg && <div className="panel" style={{background:'var(--ok-soft)', color:'var(--ok)', marginBottom:16}} data-testid="team-ok">{msg}</div>}
      {err && <div className="panel" style={{background:'var(--danger-soft)', color:'var(--danger)', marginBottom:16}} data-testid="team-err">{err}</div>}

      {inviteOpen && canManage && (
        <form className="panel" onSubmit={invite} style={{marginBottom:20}} data-testid="invite-form">
          <h3 style={{marginBottom:8}}>Nouvelle invitation</h3>
          <p style={{fontSize:13, color:'var(--ink-muted)', marginBottom:18}}>L'invité recevra un email pour créer son mot de passe et rejoindre l'équipe (lien valable 7 jours).</p>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1.2fr auto', gap:14, alignItems:'end', marginBottom:14}}>
            <div className="field" style={{margin:0}}>
              <label>Email</label>
              <input type="email" className="input" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} required data-testid="invite-email" placeholder="prenom@entreprise.pf" />
            </div>
            <div className="field" style={{margin:0}}>
              <label>Rôle</label>
              <select className="select" value={inviteRole} onChange={e=>setInviteRole(e.target.value)} data-testid="invite-role">
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div style={{display:'flex', gap:6}}>
              <button type="submit" className="btn btn-primary" disabled={loading} data-testid="invite-submit">
                {loading ? <span className="spinner"></span> : "Inviter"}
              </button>
              <button type="button" className="btn btn-ghost" onClick={()=>setInviteOpen(false)}>Annuler</button>
            </div>
          </div>
          <div style={{padding:12, background:'var(--surface-2)', borderRadius:8, fontSize:13, color:'var(--ink-muted)', borderLeft:'3px solid var(--primary)'}}>
            <strong style={{color:'var(--ink)'}}>{ROLE_OPTIONS.find(r=>r.value===inviteRole)?.label}</strong>
            <span style={{marginLeft:8}}>{ROLE_OPTIONS.find(r=>r.value===inviteRole)?.desc}</span>
          </div>
        </form>
      )}

      <div className="table-wrap" data-testid="members-table">
        <table className="table">
          <thead>
            <tr>
              <th>Membre</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Depuis</th>
              <th style={{textAlign:'right'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} data-testid={`member-${m.id}`}>
                <td>
                  <div style={{display:'flex', alignItems:'center', gap:10}}>
                    <div className="avatar">{initials(m.name)}</div>
                    <div style={{fontWeight:500}}>{m.name}{m.id===user?.id && <span style={{color:'var(--ink-muted)', fontSize:12, marginLeft:6}}>(vous)</span>}</div>
                  </div>
                </td>
                <td style={{color:'var(--ink-muted)', fontSize:13}}>{m.email}</td>
                <td><span className={`status-chip ${m.role}`}>{ROLE_LABELS[m.role]}</span></td>
                <td style={{color:'var(--ink-muted)', fontSize:13}}>{m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td style={{textAlign:'right'}}>
                  {canManage && m.role !== "owner" && m.id !== user?.id && (
                    <>
                      {user?.role === "owner" && (
                        <select className="select" style={{width:'auto', padding:'4px 8px', fontSize:12, display:'inline-block', marginRight:6}} value={m.role} onChange={e=>changeRole(m.id, e.target.value)}>
                          {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={()=>remove(m.id)} data-testid={`remove-${m.id}`}>Retirer</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <>
          <h3 style={{marginTop:30, marginBottom:10}}>Invitations en attente ({invites.length})</h3>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Email</th><th>Rôle</th><th>Expire</th><th style={{textAlign:'right'}}>Actions</th></tr></thead>
              <tbody>
                {invites.map(i => (
                  <tr key={i.id} data-testid={`invite-${i.id}`}>
                    <td>{i.email}</td>
                    <td><span className={`status-chip ${i.role}`}>{ROLE_LABELS[i.role]}</span> <span className="status-chip pending" style={{marginLeft:6}}>En attente</span></td>
                    <td style={{color:'var(--ink-muted)', fontSize:13}}>{new Date(i.expires_at).toLocaleDateString("fr-FR")}</td>
                    <td style={{textAlign:'right'}}>
                      {canManage && (
                        <>
                          <button className="btn btn-sm btn-ghost" onClick={()=>resendInvite(i.id, i.email)} data-testid={`resend-${i.id}`} style={{marginRight:6}}>Renvoyer</button>
                          <button className="btn btn-sm btn-danger" onClick={()=>cancelInvite(i.id)} data-testid={`cancel-${i.id}`}>Annuler</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
